import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Tag } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import PageHeader from "../../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import ConfirmDialog from "../../../components/carbon/ConfirmDialog/ConfirmDialog";
import { useDeleteRole, useRoles } from "../../../hooks/useRoles";
import { useNotification } from "../../../context";
import { Role } from "../../../types";
import RoleForm from "./RoleForm";
import "./roles.scss";

interface RoleRow extends DataTableRow {
  id: string;
  name: string;
  description: string;
  abilityCount: number;
  type: ReactNode;
  actions: ReactNode;
}

/**
 * The Roles page at `/roles`, reached from the user-icon menu (see `HeaderUserActions`): a table of
 * the company's roles with create / edit / delete, side-aware via the backend (buyers see buyer
 * roles, sellers seller roles). Owns its own `PageHeader` `<h1>`; shares the Carbon
 * `DataTable`/`EmptyState`/`ConfirmDialog`. Routes gate it on MANAGE_ROLES / SELLER_MANAGE_ROLES.
 *
 * Delete is only offered for custom roles (`!system && !protectedRole`). The backend rejects
 * deleting system/protected roles anyway; hiding the button is the UI guardrail. Edit is offered for
 * every role — built-ins and the Administrator included are fully editable and renamable (`RoleForm`),
 * the backend refusing only an edit that would leave nobody able to manage roles.
 */
const Roles = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const { data: roles = [], isLoading } = useRoles();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);

  const deleteRole = useDeleteRole(
    () => setPendingDelete(null),
    (error: any) =>
      openNotification(error?.response?.data?.message || t("roles.deleteError"))
  );

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (role: Role) => {
    setEditing(role);
    setFormOpen(true);
  };

  // No search on this tab, so an empty list is unconditionally the "nothing yet" case.
  const isTrulyEmpty = !isLoading && roles.length === 0;

  const addAction = (
    <Button onClick={openNew} renderIcon={Add}>
      {t("roles.add")}
    </Button>
  );

  const rows: RoleRow[] = useMemo(
    () =>
      roles.map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description ?? "",
        abilityCount: role.abilities.length,
        type: (
          <span className="roles__type">
            {role.system ? t("roles.typeDefault") : t("roles.typeCustom")}
            {role.customized ? (
              <Tag type="blue" size="sm">
                {t("roles.customized")}
              </Tag>
            ) : null}
          </span>
        ),
        actions: (
          <div className="roles__row-actions">
            <Button kind="ghost" size="sm" onClick={() => openEdit(role)}>
              {t("roles.edit")}
            </Button>
            {!role.system && !role.protectedRole ? (
              <Button
                kind="danger--ghost"
                size="sm"
                onClick={() => setPendingDelete(role)}
              >
                {t("roles.delete")}
              </Button>
            ) : null}
          </div>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles, t]
  );

  const headers: DataTableColumn<RoleRow>[] = useMemo(
    () => [
      { key: "name", header: t("roles.name") },
      { key: "description", header: t("roles.description") },
      { key: "abilityCount", header: t("roles.abilities") },
      { key: "type", header: t("roles.type") },
      { key: "actions", header: "" },
    ],
    [t]
  );

  return (
    <div>
      <PageHeader
        title={t("roles.title")}
        action={!isTrulyEmpty ? addAction : null}
      />

      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        empty={<EmptyState title={t("roles.noRoles")} action={addAction} />}
      />

      <RoleForm role={editing} open={formOpen} setOpen={setFormOpen} />

      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title={t("roles.deleteTitle")}
        body={t("roles.deleteQuestion")}
        confirmLabel={t("roles.delete")}
        onConfirm={() => pendingDelete && deleteRole.mutate(pendingDelete.id)}
        onCancel={() => setPendingDelete(null)}
        isPending={deleteRole.isPending}
      />
    </div>
  );
};

export default Roles;
